import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useProjectStore } from '../store';

/**
 * AC-471: Hook that restores project context from URL on page load/reload.
 *
 * Problem: When a user reloads while on a project-specific route (e.g., /projects/:projectId/queues),
 * the currentProject in the store is reset to null, causing the sidebar to lose project navigation.
 *
 * Solution: This hook extracts projectId from the URL and restores currentProject if needed.
 * It's designed to be called once in the Layout component to cover all project routes.
 */
export const useProjectContext = () => {
  const location = useLocation();
  const { currentProject, projects, loadProjects, setCurrentOperation } = useProjectStore();

  useEffect(() => {
    const restoreOperationContext = async () => {
      // Extract projectId from URL path (handles /projects/:projectId/*)
      const match = location.pathname.match(/\/projects\/([^/]+)/);
      const projectIdFromUrl = match ? match[1] : null;

      if (!projectIdFromUrl) {
        return; // Not on a project route
      }

      // If currentProject matches URL, no action needed
      if (currentProject && String(currentProject.project_id) === projectIdFromUrl) {
        return;
      }

      // Need to restore context
      let operationsList = projects;

      // Load projects if list is empty
      if (operationsList.length === 0) {
        await loadProjects();
        operationsList = useProjectStore.getState().projects;
      }

      // Find and set the project
      const project = operationsList.find(p => String(p.project_id) === projectIdFromUrl);
      if (project) {
        setCurrentOperation(project);
      }
    };

    restoreOperationContext();
  }, [location.pathname, currentProject, projects, loadProjects, setCurrentOperation]);
};

export default useProjectContext;
